import React, { useState } from 'react';

const SCREENS = [
  {
    name: 'Overview',
    icon: '◻',
    summary: 'High-level KPIs across all companies for a selected time period.',
    details: [
      { term: 'Active Companies / Users', def: 'Number of distinct companies and users that sent at least one query in the period.' },
      { term: 'Total Queries', def: 'Total number of questions submitted to the AI assistant.' },
      { term: 'Success Rate', def: 'Share of queries that completed without an error (backend exception or OpenSearch failure).' },
      { term: 'Total Tokens / Cost', def: 'Cumulative tokens consumed by the LLM and the estimated USD cost based on model pricing.' },
      { term: 'Avg Eval Score', def: 'Average quality score assigned by the internal evaluator node (0–1). Only populated when evaluation is enabled.' },
      { term: 'Thumbs Up %', def: 'Percentage of rated answers that received a thumbs-up from end users.' },
      { term: 'Avg Response Time', def: 'Average end-to-end time from query submission to answer delivery, in milliseconds.' },
      { term: '30-day chart', def: 'Daily query volume (bars, left axis) and cumulative cost (line, right axis) over the last 30 days.' },
    ],
  },
  {
    name: 'Companies',
    icon: '◻',
    summary: 'Per-customer breakdown — one row per company.',
    details: [
      { term: 'Company ID', def: 'Internal identifier of the customer company (MongoDB ObjectId). Matches the "company" claim in their JWT.' },
      { term: 'Queries / Users', def: 'Total queries and distinct users for that company.' },
      { term: 'Tokens / Cost', def: 'Cumulative token consumption and estimated cost for that company.' },
      { term: 'Feedback', def: 'Number of rated answers and thumbs-up percentage.' },
      { term: 'Avg Score', def: 'Average evaluator quality score for that company.' },
      { term: 'Last Active', def: 'Timestamp of the most recent query from that company.' },
      { term: 'Global company filter', def: 'Clicking a company row sets a global filter (shown in the sidebar). All other screens then show data for that company only.' },
    ],
  },
  {
    name: 'Token Usage',
    icon: '◻',
    summary: 'Detailed breakdown of LLM token consumption and cost over time.',
    details: [
      { term: 'Prompt / Completion tokens', def: 'Prompt tokens are the input sent to the model (question + context + memory). Completion tokens are the model\'s response.' },
      { term: 'Estimated cost', def: 'Calculated from token counts using the model\'s per-million-token pricing configured in the backend settings.' },
      { term: 'Model column', def: 'Which Azure OpenAI deployment answered the query (e.g. gpt-4o, gpt-5.1).' },
      { term: 'Bar chart', def: 'Token consumption per model, making it easy to spot which model drives the most usage.' },
      { term: 'CSV export', def: 'Downloads the current filtered view as a spreadsheet.' },
    ],
  },
  {
    name: 'Feedback',
    icon: '◻',
    summary: 'All thumbs-up / thumbs-down ratings submitted by end users, with full Q&A context.',
    details: [
      { term: 'Rating', def: '👍 (up) or 👎 (down) — submitted by the user directly in the chat interface after receiving an answer.' },
      { term: 'Expand row', def: 'Click a row to see the full question and AI answer that was rated, plus technical metadata (model, evaluator score, DSL query used).' },
      { term: 'Evaluator score', def: 'Automatic quality score (0–1) assigned by a second LLM pass, independent of the user\'s rating. A low score with a thumbs-up indicates the user was satisfied despite lower objective quality.' },
      { term: 'DSL success', def: 'Whether the OpenSearch query generated for this answer was syntactically valid and executed without errors.' },
      { term: 'Filter by rating', def: 'Use the Up / Down / All buttons to focus on positive or negative feedback.' },
      { term: 'CSV export', def: 'Downloads all feedback rows (with Q&A) as a spreadsheet — useful for quality reviews.' },
    ],
  },
  {
    name: 'Conversations',
    icon: '◻',
    summary: 'Full conversation threads — browse any session and replay messages.',
    details: [
      { term: 'Session', def: 'A single conversation between one user and the AI assistant. Each session has a unique ID and can contain multiple Q&A exchanges.' },
      { term: 'Replay', def: 'Expand a session to read all messages in order, exactly as the user saw them.' },
      { term: 'Delete', def: 'Permanently removes the conversation from the database. Use with caution — this cannot be undone.' },
      { term: 'Memory', def: 'The assistant maintains a rolling summary of each session. Long sessions may show a condensed memory rather than every past message.' },
    ],
  },
  {
    name: 'Traces',
    icon: '◻',
    summary: 'Full execution trace of every AI query — what happened internally, step by step.',
    details: [
      { term: 'Trace', def: 'One complete request lifecycle: from the user\'s question to the final answer. Shows which internal steps ran and how long each took.' },
      { term: 'Span', def: 'A single step inside a trace — e.g. "call the LLM", "run the search tool", "evaluate the answer". Spans can be nested.' },
      { term: 'Gantt timeline', def: 'Visual bar chart showing when each span started and how long it lasted relative to the total trace duration. Useful for spotting bottlenecks.' },
      { term: 'Node path', def: 'The sequence of LangGraph nodes the request passed through (e.g. agent → tools → agent → evaluate → end).' },
      { term: 'Duration', def: 'End-to-end time for the full trace, or for an individual span.' },
      { term: 'Input / Output', def: 'Click a span row to expand and see exactly what was sent to and received from that step (LLM prompt, tool arguments, search results, etc.).' },
      { term: 'Success', def: 'Whether the trace completed without an unhandled error. A failed trace means the user received an error message instead of an answer.' },
    ],
  },
];

const GLOSSARY = [
  { term: 'Token', def: 'The unit of text the LLM processes. Roughly 1 token ≈ 0.75 words in English. Every query consumes prompt tokens (input) and completion tokens (output).' },
  { term: 'LLM', def: 'Large Language Model — the AI model that understands questions and generates answers. Here it\'s Azure OpenAI (GPT-4o or GPT-5.1).' },
  { term: 'DSL', def: 'Domain Specific Language — the JSON query the AI generates to search OpenSearch. If the DSL is invalid, the agent retries up to 3 times.' },
  { term: 'Evaluator', def: 'An optional second LLM call that scores the quality of the answer before returning it to the user (0 = poor, 1 = excellent). Can trigger a retry if the score is too low.' },
  { term: 'Session / Session ID', def: 'A unique identifier for one conversation. The same user can have multiple sessions over time.' },
  { term: 'Company ID', def: 'The internal identifier of a customer company, taken from the JWT token they use to authenticate. Matches the MongoDB ObjectId used in the LabV platform.' },
  { term: 'Success rate', def: 'Percentage of queries that returned an answer without a backend error. A query can succeed even if the answer is wrong or the user rates it down.' },
  { term: 'LangGraph', def: 'The orchestration framework that controls the AI workflow: deciding when to call tools, when to evaluate, and when to return the final answer.' },
];

const FAQS = [
  {
    q: 'Why is the success rate below 100%?',
    a: 'A query is marked failed when an unhandled backend exception occurred — typically an OpenSearch connectivity issue, a timeout, or an invalid DSL that exhausted all retries. It does not mean the answer was wrong.',
  },
  {
    q: 'What is the difference between a low evaluator score and a thumbs-down?',
    a: 'The evaluator score is automatic and objective (did the answer address the question with data?). The thumbs rating is subjective (was the user satisfied?). They can disagree — a technically correct answer may frustrate a user, or a partial answer may be good enough.',
  },
  {
    q: 'Why do some traces have no spans?',
    a: 'Traces are captured via a LangChain callback. If the backend restarted mid-query, or the query failed very early (e.g. auth error before the agent started), the span data may be incomplete.',
  },
  {
    q: 'What does "company filter" do?',
    a: 'Clicking a company in the Companies screen sets a global filter visible in the sidebar. All screens (Token Usage, Feedback, Conversations, Traces) then show only data for that company. Click ✕ in the sidebar to clear it.',
  },
  {
    q: 'Is any data sent to external services?',
    a: 'No. The dashboard reads exclusively from the local SQLite database on the server. No analytics, telemetry, or query data is sent to third-party services.',
  },
];

function Section({ title, children }) {
  return (
    <div className="admin-help-section">
      <h2 className="admin-section-title">{title}</h2>
      {children}
    </div>
  );
}

function DefinitionList({ items }) {
  return (
    <dl className="admin-help-dl">
      {items.map(({ term, def }) => (
        <div key={term} className="admin-help-dl-row">
          <dt>{term}</dt>
          <dd>{def}</dd>
        </div>
      ))}
    </dl>
  );
}

function ScreenCard({ screen }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="admin-help-screen-card">
      <button className="admin-help-screen-header" onClick={() => setOpen(o => !o)}>
        <span className="admin-help-screen-name">{screen.name}</span>
        <span className="admin-help-screen-summary">{screen.summary}</span>
        <span className="admin-help-chevron">{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div className="admin-help-screen-body">
          <DefinitionList items={screen.details} />
        </div>
      )}
    </div>
  );
}

function FaqItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="admin-help-faq-item">
      <button className="admin-help-faq-q" onClick={() => setOpen(o => !o)}>
        <span>{faq.q}</span>
        <span className="admin-help-chevron">{open ? '▾' : '▸'}</span>
      </button>
      {open && <p className="admin-help-faq-a">{faq.a}</p>}
    </div>
  );
}

export default function AdminHelp() {
  return (
    <div className="admin-help-page">
      <h1 className="admin-section-title" style={{ marginBottom: 4 }}>Help &amp; Instructions</h1>
      <p className="admin-help-intro">
        This dashboard provides internal visibility into how the LabV AI assistant is being used
        across all customer companies. All data is stored on-premise — nothing is sent to external services.
      </p>

      <Section title="Screens">
        <div className="admin-help-screens">
          {SCREENS.map(s => <ScreenCard key={s.name} screen={s} />)}
        </div>
      </Section>

      <Section title="Glossary">
        <DefinitionList items={GLOSSARY} />
      </Section>

      <Section title="Common Questions">
        <div className="admin-help-faqs">
          {FAQS.map(f => <FaqItem key={f.q} faq={f} />)}
        </div>
      </Section>

      <Section title="Cost Estimation">
        <div className="admin-help-notice">
          <div className="admin-help-notice-title">⚠ Costs shown are estimates, not actual billing figures</div>
          <p>
            The Azure OpenAI API does not return a cost per call — costs are calculated from token counts
            multiplied by a price rate configured on the server. Azure's actual billing may differ due to
            reserved capacity, enterprise agreements, or rate changes.
          </p>
          <p>
            To keep estimates accurate, the server supports per-model pricing configured via environment variables:
          </p>
          <table className="admin-help-pricing-table">
            <thead>
              <tr><th>Variable</th><th>Applies to</th><th>Default</th></tr>
            </thead>
            <tbody>
              <tr><td>MODEL_COST_INPUT_PER_1M</td><td>All models (global fallback)</td><td>$10.00</td></tr>
              <tr><td>MODEL_COST_OUTPUT_PER_1M</td><td>All models (global fallback)</td><td>$30.00</td></tr>
              <tr><td>MODEL_COST_INPUT_PER_1M_GPT4O</td><td>GPT-4o only</td><td>uses global</td></tr>
              <tr><td>MODEL_COST_OUTPUT_PER_1M_GPT4O</td><td>GPT-4o only</td><td>uses global</td></tr>
              <tr><td>MODEL_COST_INPUT_PER_1M_GPT51</td><td>GPT-5.1 only</td><td>uses global</td></tr>
              <tr><td>MODEL_COST_OUTPUT_PER_1M_GPT51</td><td>GPT-5.1 only</td><td>uses global</td></tr>
            </tbody>
          </table>
          <p>
            To update pricing: edit the <code>.env</code> file on the server and restart the backend container
            (<code>docker compose up -d --no-deps --force-recreate backend</code>).
            Changes only affect new queries — historical rows keep the cost they were recorded with.
          </p>
        </div>
      </Section>
    </div>
  );
}
